import { ReactElement } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'
import { isEmpty } from 'lodash'
import EmptyState from 'components/v2/EmptyState'

import { StyledLink } from 'components/v2/Link'
import { TableContainer, Table, Thead, Th, Tbody, Tr } from 'components/v2/Table'
import { EmailStatus } from 'features/Bookings/components/EmailStatus'
import { SupportInteractions } from 'features/Bookings/components/SupportInteractions'
import { StyledTd, Text } from 'features/Subcampaigns/components/Table'
import { GTSubcampaignBooking } from 'gql/types.generated'
import { useLogin } from 'features/Login/hooks'
import { safeFormatMessage } from 'utils/intl'
import { canRegisterSupportInteraction, canResendCoupons } from 'features/Users/utils'
import { ITripIncidenceCouponsListProps } from './types'
import { TripIncidenceCouponsActions } from '../TripIncidenceCouponsActions'
import { couponStatusMessages } from './messages'
import { getShortId } from 'utils/string'

const StatusContainer = styled.div<TSFixMe>`
  display: flex;
  gap: 10px;
  align-items: center;
`
export const Paid = styled.div<TSFixMe>`
  border-radius: 2px;
  background-color: #ebf6ec;
  padding: 6px 10px;
  color: #419845;
  text-align: center;
  display: inline-block;
`

export const Amount = styled.div<TSFixMe>`
  border-radius: 2px;
  background-color: #fbeef3;
  padding: 6px 10px;
  color: #e53472;
  font-weight: 500;
  text-align: center;
  display: inline-block;
`

const CouponCode = styled.div<TSFixMe>`
  padding: 6px 10px;
  font-size: 13px;
  background-color: #ecebf4;
  border-radius: 4px;
  text-transform: uppercase;
  margin-right: 5px;
  display: inline-block;
`

export const TripIncidenceCouponsList = ({
  coupons,
  subcampaignId,
  campaignId
}: ITripIncidenceCouponsListProps): ReactElement => {
  const intl = useIntl()
  const login = useLogin()

  if (isEmpty(coupons)) {
    return (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="trip.incidenceCoupons.list.empty.description"
            defaultMessage="There is no incidence coupons to show"
          />
        </EmptyState.Description>
      </EmptyState.Container>
    )
  }

  const { policies } = login.accessToken
  const canSeeActions = canRegisterSupportInteraction(policies) || canResendCoupons(policies)

  return (
    <TableContainer overflow>
      <Table marginBottom>
        <Thead>
          <Tr>
            {canSeeActions && <Th />}
            <Th>
              <FormattedMessage id="trip.incidenceCoupons.list.bookings.label" defaultMessage="Bookings" />
            </Th>
            <Th>
              <FormattedMessage id="trip.incidenceCoupons.list.tickets.label" defaultMessage="Tickets" />
            </Th>
            <Th>
              <FormattedMessage id="trip.incidenceCoupons.list.buyer.label" defaultMessage="Buyer" />
            </Th>
            <Th>
              <FormattedMessage id="trip.incidenceCoupons.list.departure.label" defaultMessage="Departure" />
            </Th>
            <Th>
              <FormattedMessage id="trip.incidenceCoupons.list.arrival.label" defaultMessage="Arrival" />
            </Th>
            <Th>
              <FormattedMessage id="trip.incidenceCoupons.list.coupon.label" defaultMessage="Coupon" />
            </Th>
            <Th>
              <FormattedMessage
                id="trip.incidenceCoupons.list.supportInteractions.label"
                defaultMessage="Support interactions"
              />
            </Th>
            <Th>
              <FormattedMessage id="trip.incidenceCoupons.list.newBooking.label" defaultMessage="New Booking" />
            </Th>
            <Th>
              <FormattedMessage id="trip.incidenceCoupons.list.couponStatus.label" defaultMessage="Coupon Status" />
            </Th>
            <Th>
              <FormattedMessage id="trip.incidenceCoupons.list.emailStatus.label" defaultMessage="Email Status" />
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {coupons.map((coupon): TSFixMe => {
            return (
              <Tr key={coupon.id}>
                {canSeeActions && (
                  <StyledTd disabled={!coupon.isActive}>
                    <TripIncidenceCouponsActions
                      booking={coupon.redeemedBooking ?? (coupon.originalBooking as GTSubcampaignBooking)}
                      campaignId={campaignId}
                      subcampaignId={subcampaignId}
                      coupon={coupon}
                    />
                  </StyledTd>
                )}
                <StyledTd disabled={!coupon.isActive}>
                  <Text disabled={!coupon.isActive}>
                    <StyledLink as={NavLink} to={`/bookings?reservations=${getShortId(coupon.originalBooking?.id)}`}>
                      {coupon.originalBooking?.id.substring(0, 8)}
                    </StyledLink>
                  </Text>
                </StyledTd>
                <StyledTd disabled={!coupon.isActive}>
                  <Text disabled={!coupon.isActive}>{coupon.originalBooking?.ticketsCount}</Text>
                </StyledTd>
                <StyledTd disabled={!coupon.isActive}>
                  <Text disabled={!coupon.isActive}>
                    {!isEmpty(coupon.originalBooking?.buyer) &&
                    (coupon.originalBooking?.buyer.firstName || coupon.originalBooking?.buyer.email) ? (
                      <>
                        {coupon.originalBooking.buyer.firstName && (
                          <div>
                            {coupon.originalBooking.buyer.firstName} {coupon.originalBooking.buyer.lastName}
                          </div>
                        )}
                        {coupon.originalBooking.buyer.email && <div>{coupon.originalBooking.buyer.email}</div>}
                        {coupon.originalBooking.buyer.phone && <div>{coupon.originalBooking.buyer.phone}</div>}
                      </>
                    ) : (
                      '-'
                    )}
                  </Text>
                </StyledTd>
                <StyledTd disabled={!coupon.isActive}>
                  <Text disabled={!coupon.isActive}>{coupon.originalBooking?.origin.name}</Text>
                </StyledTd>
                <StyledTd disabled={!coupon.isActive}>
                  <Text disabled={!coupon.isActive}>{coupon.originalBooking?.destination.name}</Text>
                </StyledTd>
                <StyledTd disabled={!coupon.isActive}>
                  <Text disabled={!coupon.isActive}>
                    {coupon?.originalBooking?.coupon?.code ? (
                      <CouponCode>{coupon.originalBooking.coupon?.code}</CouponCode>
                    ) : (
                      '-'
                    )}
                  </Text>
                </StyledTd>
                <StyledTd disabled={!coupon.isActive}>
                  <Text disabled={!coupon.isActive}>
                    <SupportInteractions supportInteractions={coupon.supportInteractions} showUser />
                  </Text>
                </StyledTd>
                <StyledTd disabled={!coupon.isActive}>
                  <Text disabled={!coupon.isActive}>
                    {!isEmpty(coupon.redeemedBooking) && (
                      <StyledLink as={NavLink} to={`/bookings?reservations=${getShortId(coupon.redeemedBooking.id)}`}>
                        {coupon.redeemedBooking.id.substring(0, 8)}
                      </StyledLink>
                    )}
                  </Text>
                </StyledTd>
                <StyledTd disabled={!coupon.isActive}>
                  <Text disabled={!coupon.isActive}>
                    <StatusContainer>
                      <div>
                        {safeFormatMessage({
                          intl,
                          messages: couponStatusMessages,
                          key: coupon?.status
                        })}
                      </div>
                    </StatusContainer>
                  </Text>
                </StyledTd>
                <StyledTd disabled={!coupon.isActive}>
                  <Text disabled={!coupon.isActive}>
                    {!isEmpty(coupon.emails) && !isEmpty(coupon.emails.at(-1)) && (
                      <EmailStatus status={coupon.emails.at(-1)?.status} />
                    )}
                  </Text>
                </StyledTd>
              </Tr>
            )
          })}
        </Tbody>
      </Table>
    </TableContainer>
  )
}
